import { useEffect, useRef, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch } from "@/store"
import {
  fetchVacancyById,
  selectCurrentVacancy,
  selectVacanciesLoading,
  clearCurrentVacancy,
} from "@/modules/recruitment/vacancies/store/vacancySlice"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import {
  getJobTypeLabel,
  getLocationTypeLabel,
  getSeniorityLevelLabel,
  getDepartmentLabel,
} from "@/modules/recruitment/utils/enumHelpers"
import {
  ArrowLeft,
  MapPin,
  Briefcase,
  Banknote,
  Calendar,
  Clock,
  GraduationCap,
  Award,
  Users,
  Linkedin,
  Share2,
  Edit,
  XCircle,
  Copy,
  CopyPlus,
  Trash2,
  CheckCircle2,
  ExternalLink,
  Check,
} from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/modules/shared/ui/components/tooltip"
import { format } from "date-fns"
import { selectCurrentOrganization } from "@/modules/organizations/store/organizationSlice"
import { selectCareers24Connection } from "@/modules/profile/store/careers24ConnectorSlice"
import { WeightedSkill } from "@/modules/recruitment/entities/WeightedSkill"
import { VacancyStatus, VacancyStatusParser } from "@/modules/recruitment/enums/VacancyStatus"
import { useVacancies } from "@/modules/recruitment/hooks/useVacancies"
import { CreateVacancyDialog } from "../components/CreateVacancyDialog"
import { DeleteConfirmationDialog } from "../components/DeleteConfirmationDialog"
import { SendRegretDialog } from "../components/SendRegretDialog"
import { DuplicateVacancyDialog } from "../components/DuplicateVacancyDialog"
import { Careers24ResultModal } from "../components/Careers24ResultModal"
import { VacancyListItemDto } from "@/modules/recruitment/dtos/VacancyListItemDto"
import { flushSync } from "react-dom"
import { toast } from "sonner"
import { buildLinkedInShareText } from "@/modules/recruitment/vacancies/lib/build-linkedin-share-text"

export default function VacancyDetailsPage() {
  const { vacancyId } = useParams<{ vacancyId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const vacancy = useSelector(selectCurrentVacancy)
  const isLoading = useSelector(selectVacanciesLoading)
  const organization = useSelector(selectCurrentOrganization)
  const careers24Connection = useSelector(selectCareers24Connection)

  const {
    createNewVacancy,
    updateVacancyStatusById,
    notifyRegretCandidates,
    deleteVacancyById,
    postVacancyToCareers24,
    isCareers24Loading,
  } = useVacancies()

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false)
  const [isContinuing, setIsContinuing] = useState(false)
  const [showSendRegretDialog, setShowSendRegretDialog] = useState(false)
  const [careers24Result, setCareers24Result] = useState<{ success: boolean; message?: string; vacancyUrl?: string } | null>(null)
  const [isCareers24ModalOpen, setIsCareers24ModalOpen] = useState(false)
  const isEditMode = useRef(true)
  const [linkCopied, setLinkCopied] = useState(false)

  const handleCopyCareers24Link = (url: string) => {
    navigator.clipboard.writeText(url)
    setLinkCopied(true)
    toast.success("Careers24 link copied!")
    setTimeout(() => setLinkCopied(false), 2000)
  }

  const vacancyAsListItem = vacancy ? (vacancy as unknown as VacancyListItemDto) : null
  const isClosed = vacancy?.status === VacancyStatus.Closed
  const isSharingBlocked = vacancy?.status === VacancyStatus.Closed || vacancy?.status === VacancyStatus.Expired
  const careers24Posting = vacancy?.externalPostings?.find((p) => p.platform === "Careers24")

  const handleCloseVacancy = async () => {
    if (!vacancy || !organization) return
    try {
      await updateVacancyStatusById(vacancy.id, VacancyStatus.Closed)
      toast.success("Vacancy closed successfully")
      setShowSendRegretDialog(true)
      dispatch(fetchVacancyById({ organizationId: organization.id, id: vacancy.id }))
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to close vacancy")
    }
  }

  const handleSendRegretMessages = async () => {
    if (!vacancy) return
    try {
      const emailsSent = await notifyRegretCandidates(vacancy.id)
      if (emailsSent === 0) toast.info("No candidates to notify")
      else toast.success(`Regret messages sent to ${emailsSent} candidates`)
    } catch (error: unknown) {
      toast.error((error as Error)?.message ?? "Failed to send regret messages")
    }
  }

  const handleDeleteVacancy = async () => {
    if (!vacancy) return
    setIsDeleting(true)
    try {
      await deleteVacancyById(vacancy.id)
      toast.success("Vacancy deleted successfully")
      navigate("/recruitment/vacancies")
    } catch (error: unknown) {
      toast.error((error as Error)?.message ?? "Failed to delete vacancy")
    } finally {
      setIsDeleting(false)
    }
  }

  const handleShareToLinkedIn = () => {
    if (!vacancy || !organization) return
    if (!import.meta.env.VITE_APPLY_LINK) {
      toast.error("Something went wrong, please try again later")
      return
    }
    const { text: trimmed, raw, applyLink } = buildLinkedInShareText({
      vacancy,
      organizationId: organization.id,
      organizationName: organization.name,
      applyLinkBase: import.meta.env.VITE_APPLY_LINK,
    })
    navigate(`/linkedin-share-preview?text=${encodeURIComponent(raw)}`, {
      state: { raw, trimmed, applyLink },
    })
  }

  const handleShareToCareers24 = async () => {
    if (!vacancy) return
    flushSync(() => { setCareers24Result(null); setIsCareers24ModalOpen(true) })
    try {
      const result = await postVacancyToCareers24(vacancy.id)
      setCareers24Result(result)
    } catch (error: unknown) {
      setIsCareers24ModalOpen(false)
      toast.error((error as Error)?.message ?? "Failed to post vacancy to Careers24")
    }
  }

  const handleDuplicateVacancy = async () => {
    if (!vacancy || !organization) return
    try {
      await createNewVacancy({
        title: vacancy.title,
        description: vacancy.description ?? "",
        jobType: vacancy.jobType,
        department: vacancy.department,
        locationType: vacancy.locationType,
        seniorityLevel: vacancy.seniorityLevel,
        minimumSalary: vacancy.minimumSalary,
        maximumSalary: vacancy.maximumSalary,
        yearsOfExperience: vacancy.yearsOfExperience ?? 0,
        educationLevel: vacancy.educationLevel ?? "",
        keyResponsibilities: vacancy.keyResponsibilities ?? [],
        requiredSkills: vacancy.requiredSkills ?? [],
        optionalSkills: vacancy.optionalSkills ?? [],
        certifications: vacancy.certifications ?? [],
        applicationDeadline: vacancy.applicationDeadline,
        baseScore: vacancy.baseScore ?? 0,
        organizationId: organization.id,
        status: VacancyStatusParser(vacancy.status),
      })
      toast.success("Vacancy duplicated successfully")
    } catch (error: unknown) {
      toast.error((error as Error)?.message ?? "Failed to duplicate vacancy")
    }
  }

  const vacancyActions = vacancy ? [
    { icon: Users, label: "View Applications", description: "See all applicants for this vacancy", onClick: () => navigate(`/recruitment/vacancies/${vacancy.id}/applicants`, { state: { vacancyTitle: vacancy.title, baseScore: vacancy.baseScore } }), disabled: false, color: "text-violet-500" },
    { icon: Linkedin, label: "Share to LinkedIn", description: "Post this vacancy on LinkedIn", onClick: handleShareToLinkedIn, disabled: isSharingBlocked, color: "text-sky-600" },
    { icon: Copy, label: "Copy Application Link", description: "Copy the direct application link", onClick: () => {
      if (!import.meta.env.VITE_APPLY_LINK) {
        toast.error("Something went wrong, please try again later")
        return
      }
      const token = btoa(`${vacancy.id}|${organization!.id}`).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
      navigator.clipboard.writeText(import.meta.env.VITE_APPLY_LINK + token)
      toast.success("Application link copied!")
    }, disabled: isSharingBlocked, color: "text-indigo-500" },
    ...(careers24Connection ? [{
      icon: Share2,
      label: careers24Posting ? "Posted on Careers24" : "Share to Careers24",
      description: careers24Posting
        ? `Live since ${new Date(careers24Posting.postedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} — click to copy link`
        : "Post this vacancy to Careers24",
      onClick: () => {
        if (careers24Posting) {
          if (careers24Posting.postingUrl) {
            handleCopyCareers24Link(careers24Posting.postingUrl)
          } else {
            toast.info("Your vacancy is shared, please check on Careers24")
          }
        } else if (!isSharingBlocked) {
          handleShareToCareers24()
        }
      },
      disabled: !careers24Posting && isSharingBlocked,
      color: careers24Posting ? "text-teal-600" : "text-teal-500",
    }] : []),
    { icon: Edit, label: "Edit Vacancy", description: "Modify the vacancy details", onClick: () => setIsEditDialogOpen(true), disabled: isClosed, color: "text-amber-500" },
    { icon: XCircle, label: "Close Vacancy", description: "Mark this vacancy as closed", onClick: handleCloseVacancy, disabled: isClosed, color: "text-orange-500" },
    { icon: CopyPlus, label: "Duplicate Vacancy", description: "Create a copy of this vacancy", onClick: () => setIsDuplicateDialogOpen(true), disabled: false, color: "text-muted-foreground" },
    { icon: Trash2, label: "Delete Vacancy", description: "Permanently remove this vacancy", onClick: () => setIsDeleteDialogOpen(true), disabled: false, color: "text-destructive" },
  ] : []

  useEffect(() => {
    if (vacancyId && organization) {
      dispatch(
        fetchVacancyById({ organizationId: organization.id, id: vacancyId }),
      )
    }
    return () => {
      dispatch(clearCurrentVacancy())
    }
  }, [vacancyId, organization, dispatch])

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-700 hover:bg-green-100"
      case "published":
        return "bg-primary/10 text-primary hover:bg-primary/10"
      case "closed":
        return "bg-orange-100 text-orange-700 hover:bg-orange-100"
      case "expired":
        return "bg-red-100 text-red-700 hover:bg-red-100"
      default:
        return "bg-muted text-foreground hover:bg-muted"
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
      amount,
    )
  }

  const getSkillLabel = (skill: WeightedSkill | string) => {
    if (typeof skill === "string") return skill
    if (skill.weight === undefined || skill.weight === null) return skill.name
    return `${skill.name} (${skill.weight})`
  }

  if (isLoading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  if (!vacancy) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <h2 className="text-xl font-semibold text-foreground">
          Vacancy not found
        </h2>
        <Button
          variant="outline"
          onClick={() => navigate("/recruitment/vacancies")}
        >
          Back to List
        </Button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="space-y-8 max-w-6xl mx-auto pb-12 px-4 sm:px-6 lg:px-8 pt-6">
        {/* Header Section with Enhanced Styling */}
        <div className="flex flex-col gap-6">
          <Button
            variant="ghost"
            className="w-fit pl-0 hover:text-primary text-muted-foreground transition-all duration-200"
            onClick={() => navigate("/recruitment/vacancies")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Vacancies
          </Button>

          {/* Careers24 Posted Banner */}
          {careers24Posting && (
            <div className="flex items-center justify-between gap-3 bg-teal-50 border border-teal-200 rounded-xl px-5 py-3">
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-teal-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-teal-800">Job posted on Careers24</p>
                  <p className="text-xs text-teal-600">
                    Live since {new Date(careers24Posting.postedAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-teal-700 truncate max-w-[260px] hidden sm:block">{careers24Posting.postingUrl}</span>
                <button
                  onClick={() => careers24Posting.postingUrl ? handleCopyCareers24Link(careers24Posting.postingUrl) : toast.info("Your vacancy is shared, please check on Careers24")}
                  className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-teal-100 hover:bg-teal-200 text-teal-800 transition-colors border border-teal-300"
                >
                  {linkCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {linkCopied ? "Copied!" : "Copy link"}
                </button>
                <a
                  href={careers24Posting.postingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-card hover:bg-teal-50 text-teal-700 transition-colors border border-teal-300"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  View
                </a>
              </div>
            </div>
          )}

          {/* Enhanced Header Card */}
          <div className="bg-card rounded-xl shadow-lg border border-border p-8 relative overflow-hidden">
            {/* Decorative gradient overlay */}
            <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary/5 to-transparent opacity-50 rounded-xl" />

            <div className="relative z-10 flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-4xl font-bold text-foreground tracking-tight">
                    {vacancy.title}
                  </h1>
                  <Badge
                    className={`${getStatusColor(vacancy.status)} text-sm px-4 py-1.5 rounded-full font-medium shadow-sm`}
                  >
                    {vacancy.status}
                  </Badge>
                </div>
                <p className="text-muted-foreground mt-3 text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary" />
                  {vacancy.department}
                </p>
              </div>
              <TooltipProvider>
                <div className="flex items-center gap-1.5 shrink-0 ml-4">
                  {vacancyActions.map((action, idx) => {
                    const Icon = action.icon
                    return (
                      <Tooltip key={idx}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={action.onClick}
                            disabled={action.disabled}
                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                              action.disabled
                                ? "bg-muted border border-border opacity-40 cursor-not-allowed"
                                : "bg-muted border border-border hover:scale-110 hover:bg-card hover:border-border cursor-pointer"
                            }`}
                          >
                            <Icon className={`w-4 h-4 ${action.disabled ? "text-muted-foreground" : action.color}`} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[200px] p-0 overflow-hidden">
                          <div className="px-3.5 py-2.5 space-y-1">
                            <div className="flex items-center gap-2">
                              <Icon className={`w-3.5 h-3.5 shrink-0 ${action.color}`} />
                              <span className="text-[13px] font-semibold text-white leading-tight">{action.label}</span>
                            </div>
                            <p className="text-[11px] leading-relaxed text-muted-foreground">{action.description}</p>
                            {action.disabled && (
                              <div className="inline-flex items-center gap-1 mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium bg-white/10 text-muted-foreground">
                                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                                Not available for closed or expired vacancies
                              </div>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    )
                  })}
                </div>
              </TooltipProvider>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description Card with Enhanced Styling */}
            <Card className="bg-card shadow-md hover:shadow-lg transition-shadow duration-300 border-border">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent border-b border-border">
                <CardTitle className="text-2xl text-foreground flex items-center gap-2">
                  <div className="w-1 h-6 bg-primary rounded-full" />
                  About the Role
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="prose max-w-none text-foreground whitespace-pre-wrap leading-relaxed">
                  {vacancy.description}
                </div>
              </CardContent>
            </Card>

            {/* Job Details from Job Description */}
            {vacancy && (
              <Card className="bg-card shadow-md hover:shadow-lg transition-shadow duration-300 border-border">
                <CardHeader className="bg-gradient-to-r from-muted/30 to-transparent border-b border-border">
                  <CardTitle className="text-2xl text-foreground flex items-center gap-2">
                    <div className="w-1 h-6 bg-muted-foreground rounded-full" />
                    Job Specification
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-8 pt-6">
                  {vacancy.keyResponsibilities?.length > 0 && (
                    <div className="bg-muted/30 rounded-lg p-6 border border-border">
                      <h3 className="font-semibold text-foreground mb-4 text-lg flex items-center gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full" />
                        Key Responsibilities
                      </h3>
                      <ul className="list-none space-y-3 text-foreground">
                        {vacancy.keyResponsibilities.map((skill, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <span className="text-primary font-bold mt-1">
                              •
                            </span>
                            <span>
                              {skill.name} ({skill.weight})
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="bg-primary/5 rounded-lg p-6 border border-primary/10">
                    <h3 className="font-semibold text-foreground mb-4 text-lg flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full" />
                      Skills & Qualifications
                    </h3>
                    <div className="space-y-6">
                      {vacancy.requiredSkills?.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">
                            Required Skills
                          </p>
                          <div className="flex flex-wrap gap-2 min-w-0">
                            {vacancy.requiredSkills.map((skill, idx) => (
                              <Badge
                                key={idx}
                                className="bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-1.5 shadow-sm transition-all hover:scale-105 whitespace-normal"
                              >
                                {skill.name} ({skill.weight})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {(vacancy.optionalSkills?.length ?? 0) > 0 && (
                        <div>
                          <p className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">
                            Nice to Have
                          </p>
                          <div className="flex flex-wrap gap-2 min-w-0">
                            {vacancy.optionalSkills.map((skill, idx) => (
                              <Badge
                                key={idx}
                                variant="secondary"
                                className="bg-card text-foreground hover:bg-muted border border-border px-3 py-1.5 shadow-sm transition-all hover:scale-105 whitespace-normal"
                              >
                                {skill.name} ({skill.weight})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar with Enhanced Styling */}
          <div className="space-y-8">
            <Card className="bg-card shadow-md hover:shadow-lg transition-shadow duration-300 border-border">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent border-b border-border">
                <CardTitle className="text-xl text-foreground flex items-center gap-2">
                  <div className="w-1 h-5 bg-primary rounded-full" />
                  Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 pt-6">
                <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Briefcase className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Job Type
                    </p>
                    <p className="text-foreground font-semibold text-lg">
                      {getJobTypeLabel(vacancy.jobType)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Briefcase className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Department
                    </p>
                    <p className="text-foreground font-semibold text-lg">
                      {getDepartmentLabel(vacancy.department)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Location
                    </p>
                    <p className="text-foreground font-semibold text-lg">
                      {getLocationTypeLabel(vacancy.locationType)}
                    </p>
                  </div>
                </div>

                {(vacancy.minimumSalary > 0 || vacancy.maximumSalary > 0) && (
                  <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Banknote className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        Annual Salary Range
                      </p>
                      <p className="text-foreground font-semibold text-lg">
                        {formatCurrency(vacancy.minimumSalary)} -{" "}
                        {formatCurrency(vacancy.maximumSalary)}{" "}
                        <span className="text-muted-foreground">
                          ({vacancy.currency})
                        </span>
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      Posted Date
                    </p>
                    <p className="text-foreground font-semibold text-lg">
                      {format(new Date(vacancy.createdAt), "MMMM d, yyyy")}
                    </p>
                  </div>
                </div>

                {vacancy.applicationDeadline && (
                  <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Clock className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        Deadline
                      </p>
                      <p className="text-foreground font-semibold text-lg">
                        {format(
                          new Date(vacancy.applicationDeadline),
                          "MMMM d, yyyy",
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {vacancy && (
              <Card className="bg-card shadow-md hover:shadow-lg transition-shadow duration-300 border-border">
                <CardHeader className="bg-gradient-to-r from-muted/30 to-transparent border-b border-border">
                  <CardTitle className="text-xl text-foreground flex items-center gap-2">
                    <div className="w-1 h-5 bg-muted-foreground rounded-full" />
                    Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5 pt-6">
                  <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Award className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        Experience
                      </p>
                      <p className="text-foreground font-semibold text-lg">
                        {vacancy.yearsOfExperience} Years
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Award className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        Base Score
                      </p>
                      <p className="text-foreground font-semibold text-lg">
                        {vacancy.baseScore}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <span className="text-primary font-bold text-sm">
                          Sr
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        Seniority
                      </p>
                      <p className="text-foreground font-semibold text-lg">
                        {getSeniorityLevelLabel(vacancy.seniorityLevel)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <GraduationCap className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        Education
                      </p>
                      <p className="text-foreground font-semibold text-lg">
                        {vacancy.educationLevel}
                      </p>
                    </div>
                  </div>
                  {vacancy.certifications.length > 0 && (
                    <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Award className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                          Certifications
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {vacancy.certifications.map((certification, idx) => (
                            <Badge
                              key={idx}
                              variant="secondary"
                              className="bg-card text-foreground hover:bg-muted border border-border px-3 py-1.5 shadow-sm transition-all hover:scale-105 whitespace-normal"
                            >
                              {getSkillLabel(certification)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      <CreateVacancyDialog
        open={isEditDialogOpen}
        onOpenChange={(open) => { setIsEditDialogOpen(open) }}
        isEditMode={isEditMode.current}
        vacancy={vacancyAsListItem ?? undefined}
      />
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteVacancy}
        title="Delete Vacancy"
        description={`Are you sure you want to delete "${vacancy?.title}"? This action cannot be undone.`}
        isDeleting={isDeleting}
      />
      <SendRegretDialog
        open={showSendRegretDialog}
        onOpenChange={setShowSendRegretDialog}
        onConfirm={handleSendRegretMessages}
        vacancyTitle={vacancy?.title}
      />
      <DuplicateVacancyDialog
        open={isDuplicateDialogOpen}
        onOpenChange={(open) => { setIsDuplicateDialogOpen(open) }}
        vacancyTitle={vacancy?.title}
        isContinuing={isContinuing}
        onEdit={() => { setIsDuplicateDialogOpen(false); setIsEditDialogOpen(true); isEditMode.current = false }}
        onContinue={async () => {
          setIsContinuing(true)
          try { await handleDuplicateVacancy() }
          finally { setIsContinuing(false); setIsDuplicateDialogOpen(false) }
        }}
      />
      <Careers24ResultModal
        open={isCareers24ModalOpen}
        onOpenChange={(open) => { setIsCareers24ModalOpen(open); if (!open) setCareers24Result(null) }}
        result={careers24Result}
        isLoading={isCareers24Loading}
      />
    </div>
  )
}
